import { useEffect, useState } from 'react';
import css from './index.module.scss';
import Images from '@assets/images';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Button, Divider, IconNode, ToolTip } from '@components/base';
import { TextField } from '@components/common';
import {
  calculateCFRPrice,
  calculateCIFPrice,
  calculateDdpPrice,
  calculateExWorksPrice,
  calculateFobPrice,
  calculateCMValues,
  EMPTY_CM_VALUES,
  ICMValues,
  calculateQuoteCost,
  calculateQuotedPrice,
  convertPricePerUnit,
  convertWeight,
  formatPortOptions,
  getCommaSeperatedCurrencyAmount,
  roundTo2
} from '@helpers/utils';
import { createFilterOptions } from '@mui/material';
import Typography from '@components/base/typography';
import QuoteProductDetails from '../../../quote-product-details';
import QuoteIncoTerm from '../../../quote-inco-term';
import QuoteBaseExWorksPrice from '../../../quote-base-exworks';
import QuoteFreightPort from '../../../quote-freight-port';
import QuotePackageWrapper from '../../../quote-package-wrapper';
import { ICurrency, IFilterState, IWeightChildOption } from '@helpers/types/quotes';
import QuotePricePackageWrapper from '../../../quote-price-package-wrapper';
import { DEFAULT_PRIMARY_UNIT } from '../../../helper';
import QuoteDdpPrice from '../../../quoteDdp';

const filter = createFilterOptions<any>();

interface INonConsolidationQuoteProps {
  isEnquiry?: boolean;
  index: number;
  isExpanded: string;
  productName: string;
  productPackaging: string;
  dealQuantity: string;
  dealQuantityUnit: string;
  basePrice: string;
  basePriceCurrency: string;
  fobPrice: string;
  fobPriceCurrency: string;
  fobPriceManualOverride: string;
  ddpPrice: string;
  ddpPriceCurrency: string;
  ddpPriceWeightUnit: string;
  ddpPriceManualOverride: string;
  moq: string;
  moqUnit: string;
  totalWeight: string;
  totalWeightUnit: string;
  totalLocalFreight: string;
  totalLocalFreightCurrency: string;
  totalPortAndClearanceExpenses: string;
  totalPortAndClearanceExpensesCurrency: string;
  totalFreight: string;
  totalFreightCurrency: string;
  portOfLoading: string;
  portOfDischarge: string;
  insuranceCost: string;
  additionalCost: string;
  additionalCostCurrency: string;
  financeCost: string;
  profitMarginExpected: string;
  discount: string;
  tariff: string;
  incoTerms: string;
  incoTermsLocation: string;
  incoTermsZipCode: string;
  switchBlCost: string;
  switchBLCostCurrency: string;
  cifPrice: string;
  cfrPrice: string;
  cifPriceCurrency: string;
  cfrPriceCurrency: string;
  cifPriceManualOverride: string;
  cfrPriceManualOverride: string;
  exWorksPriceManualOverride: string;
  finalExWorksPrice: string;
  finalExWorksPriceCurrency: string;
  finalQuoteSellingPrice: string;
  finalQuoteSellingPriceWeightUnit: string;
  countryOfOrigin: string;
  materialLocationCountry: string;
  basePriceWeightUnit: string;
  fobPriceWeightUnit: string;
  cifPriceWeightUnit: string;
  cfrPriceWeightUnit: string;
  finalExWorksPriceWeightUnit: string;
  drayageChargeCurrency: string;
  drayageCharge: string;
  isfFilingCurrency: string;
  isfFiling: string;
  customClearance: string;
  warehousingCostPrice: string;
  warehousingCostCurrency: string;
  containerSize: string;

  // Functions
  handleDeleteClick?: () => void;
  handleDuplicateQuote?: (index: number) => void;
  filterProductOptions: (event: any, param?: any) => Promise<void>;
  filterPorts: (event?: any, param?: any, fieldType?: string) => Promise<void>;

  // Arrays and objects
  fieldLength?: number;
  filterState: IFilterState;
  selectedWeightSystemChildOptions: IWeightChildOption[];
  selectedCurrency: ICurrency[];
  productOptions: any[];
  portOfLoadingOptions: any[];
  portOfDischargeOptions: any[];

  // Additional props
  handleAddFields?: () => void;
  primaryUnitWatch: IWeightChildOption;
  currency1Watch: ICurrency;
  fxRateWatch: number;
  disableProductName?: boolean;
}

const NonConsolidationQuote = (props: INonConsolidationQuoteProps) => {
  const {
    isEnquiry,
    index,
    productName,
    productPackaging,
    dealQuantity,
    basePrice,
    fobPrice,
    moq,
    moqUnit,
    totalWeight,
    totalLocalFreight,
    totalPortAndClearanceExpenses,
    totalFreight,
    portOfLoading,
    portOfDischarge,
    insuranceCost,
    additionalCost,
    financeCost,
    profitMarginExpected,
    discount,
    tariff,
    incoTerms,
    switchBlCost,
    switchBLCostCurrency,
    handleDeleteClick,
    fieldLength = 1,
    handleDuplicateQuote,
    dealQuantityUnit,
    basePriceCurrency,
    fobPriceCurrency,
    totalWeightUnit,
    totalLocalFreightCurrency,
    totalPortAndClearanceExpensesCurrency,
    totalFreightCurrency,
    additionalCostCurrency,
    cifPrice,
    cfrPrice,
    cifPriceCurrency,
    cfrPriceCurrency,
    filterState,
    selectedCurrency,
    selectedWeightSystemChildOptions,
    finalExWorksPrice,
    finalExWorksPriceCurrency,
    finalQuoteSellingPrice,
    isExpanded,
    filterProductOptions,
    productOptions,
    filterPorts,
    portOfLoadingOptions,
    portOfDischargeOptions,
    countryOfOrigin,
    materialLocationCountry,
    fobPriceManualOverride,
    cfrPriceManualOverride,
    cifPriceManualOverride,
    finalQuoteSellingPriceWeightUnit,
    incoTermsLocation,
    incoTermsZipCode,
    exWorksPriceManualOverride,
    primaryUnitWatch,
    fxRateWatch,
    currency1Watch,
    basePriceWeightUnit,
    fobPriceWeightUnit,
    cifPriceWeightUnit,
    cfrPriceWeightUnit,
    finalExWorksPriceWeightUnit,
    ddpPrice,
    ddpPriceCurrency,
    ddpPriceWeightUnit,
    ddpPriceManualOverride,
    drayageChargeCurrency,
    drayageCharge,
    isfFilingCurrency,
    isfFiling,
    customClearance,
    warehousingCostCurrency,
    warehousingCostPrice,
    disableProductName = false,
    containerSize
  } = props;

  const { fcl, mtt } = filterState;
  const { control, setValue, getValues, resetField } = useFormContext();
  const [cmValues, setCmValues] = useState<ICMValues>(EMPTY_CM_VALUES);
  // Not part of the payload; kept in form state so the cost calculator can show CM1 / CM2
  const cmValuesField = profitMarginExpected.replace(/profitMarginExpected$/, 'cmValues');

  const basePriceWatch = useWatch({
    control,
    name: basePrice
  });

  const basePriceCurrencyWatch = useWatch({
    control,
    name: basePriceCurrency
  });

  const finalExWorksPriceWatch = useWatch({
    control,
    name: finalExWorksPrice
  });

  const finalExWorksPriceCurrencyWatch = useWatch({
    control,
    name: finalExWorksPriceCurrency
  });

  const dealQuantityWatch = useWatch({
    control,
    name: dealQuantity
  });

  const dealQuantityUnitWatch = useWatch({
    control,
    name: dealQuantityUnit
  });

  const totalLocalFreightWatch = useWatch({
    control,
    name: totalLocalFreight
  });

  const totalLocalFreightCurrencyWatch = useWatch({
    control,
    name: totalLocalFreightCurrency
  });

  const totalPortAndClearanceExpensesWatch = useWatch({
    control,
    name: totalPortAndClearanceExpenses
  });

  const totalPortAndClearanceExpensesCurrencyWatch = useWatch({
    control,
    name: totalPortAndClearanceExpensesCurrency
  });

  const totalWeightWatch = useWatch({
    control,
    name: totalWeight
  });

  const totalWeightUnitWatch = useWatch({
    control,
    name: totalWeightUnit
  });

  const fobPriceCurrencyWatch = useWatch({
    control,
    name: fobPriceCurrency
  });

  const fobPriceWatch = useWatch({
    control,
    name: fobPrice
  });

  const fobPriceManualOverrideWatch = useWatch({
    control,
    name: fobPriceManualOverride
  });

  const totalFreightWatch = useWatch({
    control,
    name: totalFreight
  });

  const totalFreightCurrencyWatch = useWatch({
    control,
    name: totalFreightCurrency
  });

  const cifPriceWatch = useWatch({
    control,
    name: cifPrice
  });

  const cifPriceManualOverrideWatch = useWatch({
    control,
    name: cifPriceManualOverride
  });

  const cfrPriceManualOverrideWatch = useWatch({
    control,
    name: cfrPriceManualOverride
  });

  const exWorksPriceManualOverrideWatch = useWatch({
    control,
    name: exWorksPriceManualOverride
  });

  const additionalCostWatch = useWatch({
    control,
    name: additionalCost
  });

  const additionalCostCurrencyWatch = useWatch({
    control,
    name: additionalCostCurrency
  });

  const financeCostWatch = useWatch({
    control,
    name: financeCost
  });

  const profitMarginExpectedWatch = useWatch({
    control,
    name: profitMarginExpected
  });

  const discountWatch = useWatch({
    control,
    name: discount
  });

  const tariffWatch = useWatch({
    control,
    name: tariff
  });

  const cfrPriceCurrencyWatch = useWatch({
    control,
    name: cfrPriceCurrency
  });

  const cfrPriceWatch = useWatch({
    control,
    name: cfrPrice
  });

  const insuranceCostWatch = useWatch({
    control,
    name: insuranceCost
  });

  const cifPriceCurrencyWatch = useWatch({
    control,
    name: cifPriceCurrency
  });

  const switchBLCostWatch = useWatch({
    control,
    name: switchBlCost
  });

  const switchBLCostCurrencyWatch = useWatch({
    control,
    name: switchBLCostCurrency
  });

  const isExpandedWatch = useWatch({
    control,
    name: isExpanded
  });

  const productWatch = useWatch({
    control,
    name: productName
  });

  const incotermWatch = useWatch({
    name: incoTerms,
    control
  });

  const finalQuoteSellingPriceWeightUnitWatch = useWatch({
    name: finalQuoteSellingPriceWeightUnit,
    control
  });

  const basePriceWeightUnitWatch = useWatch({
    name: basePriceWeightUnit,
    control
  });

  const fobPriceWeightUnitWatch = useWatch({
    name: fobPriceWeightUnit,
    control
  });

  const cifPriceWeightUnitWatch = useWatch({
    name: cifPriceWeightUnit,
    control
  });

  const cfrPriceWeightUnitWatch = useWatch({
    name: cfrPriceWeightUnit,
    control
  });

  const finalExWorksPriceWeightUnitWatch = useWatch({
    name: finalExWorksPriceWeightUnit,
    control
  });

  const incoTermsZipCodeWatch = useWatch({
    control,
    name: incoTermsZipCode
  });

  const ddpPriceWatch = useWatch({
    control,
    name: ddpPrice
  });

  const ddpPriceCurrencyWatch = useWatch({
    control,
    name: ddpPriceCurrency
  });

  const ddpPriceWeightUnitWatch = useWatch({
    control,
    name: ddpPriceWeightUnit
  });

  const ddpPriceManualOverrideWatch = useWatch({
    control,
    name: ddpPriceManualOverride
  });

  const drayageChargeWatch = useWatch({
    control,
    name: drayageCharge
  });

  const drayageChargeCurrencyWatch = useWatch({
    control,
    name: drayageChargeCurrency
  });

  const isfFilingWatch = useWatch({
    control,
    name: isfFiling
  });

  const isfFilingCurrencyWatch = useWatch({
    control,
    name: isfFilingCurrency
  });

  const customClearanceWatch = useWatch({
    control,
    name: customClearance
  });

  const warehousingCostPriceWatch = useWatch({
    control,
    name: warehousingCostPrice
  });

  const warehousingCostCurrencyWatch = useWatch({
    control,
    name: warehousingCostCurrency
  });

  // Ex-Works Price Calculation
  useEffect(() => {
    if (exWorksPriceManualOverrideWatch) return;

    if (!basePriceWatch || !fxRateWatch) {
      return;
    }

    const exWorksPrice = calculateExWorksPrice(
      basePriceCurrencyWatch,
      currency1Watch,
      basePriceWatch,
      fxRateWatch || 1,
      finalExWorksPriceCurrencyWatch,
      basePriceWeightUnitWatch,
      finalExWorksPriceWeightUnitWatch,
      primaryUnitWatch
    );
    setValue(exWorksPriceManualOverride, false);
    setValue(fobPriceManualOverride, false);
    setValue(finalExWorksPrice, exWorksPrice);
  }, [
    basePriceWatch,
    fxRateWatch,
    currency1Watch,
    basePriceCurrencyWatch,
    finalExWorksPriceCurrencyWatch,
    exWorksPriceManualOverrideWatch,
    basePriceWeightUnitWatch,
    finalExWorksPriceWeightUnitWatch
  ]);

  // FOB Price Calculation
  useEffect(() => {
    if (fobPriceManualOverrideWatch) return;

    if (!fxRateWatch) {
      return;
    }

    const dealQuantityInPrimaryUnit = convertWeight(
      dealQuantityWatch || 0,
      dealQuantityUnitWatch?.value,
      primaryUnitWatch?.value
    );

    const totalWeightInPrimaryUnit = convertWeight(
      totalWeightWatch || 0,
      totalWeightUnitWatch?.value,
      primaryUnitWatch?.value
    );

    const weightToUse =
      dealQuantityInPrimaryUnit == 0
        ? totalWeightInPrimaryUnit
        : totalWeightInPrimaryUnit == 0
        ? dealQuantityInPrimaryUnit
        : Math.min(dealQuantityInPrimaryUnit, totalWeightInPrimaryUnit);

    if (!weightToUse || weightToUse <= 0) {
      return;
    }

    const finalFobPrice = calculateFobPrice(
      totalLocalFreightWatch,
      totalLocalFreightCurrencyWatch,
      totalPortAndClearanceExpensesWatch,
      totalPortAndClearanceExpensesCurrencyWatch,
      weightToUse,
      fxRateWatch || 1,
      currency1Watch,
      fobPriceCurrencyWatch,
      finalExWorksPriceWatch,
      finalExWorksPriceCurrencyWatch,
      finalExWorksPriceWeightUnitWatch,
      fobPriceWeightUnitWatch,
      primaryUnitWatch
    );
    setValue(fobPriceManualOverride, false);
    setValue(cfrPriceManualOverride, false);
    const newValue = finalFobPrice;
    const currentValue = getValues(fobPrice);
    if (newValue !== currentValue) {
      setValue(fobPrice, finalFobPrice);
    } else return;
  }, [
    totalLocalFreightWatch,
    totalLocalFreightCurrencyWatch,
    totalPortAndClearanceExpensesWatch,
    totalPortAndClearanceExpensesCurrencyWatch,
    totalWeightWatch,
    totalWeightUnitWatch,
    finalExWorksPriceWatch,
    primaryUnitWatch,
    fobPriceCurrencyWatch,
    fxRateWatch,
    finalExWorksPriceCurrency,
    fobPriceManualOverrideWatch,
    dealQuantityUnitWatch,
    dealQuantityWatch,
    finalExWorksPriceWeightUnitWatch,
    fobPriceWeightUnitWatch
  ]);

  // CFR Price
  useEffect(() => {
    if (cfrPriceManualOverrideWatch) return;

    if (!fxRateWatch) {
      return;
    }

    const dealQuantityInPrimaryUnit = convertWeight(
      dealQuantityWatch || 0,
      dealQuantityUnitWatch?.value || '',
      primaryUnitWatch?.value
    );

    const totalWeightInPrimaryUnit = convertWeight(
      totalWeightWatch || 0,
      totalWeightUnitWatch?.value,
      primaryUnitWatch?.value
    );

    const weightToUse =
      dealQuantityInPrimaryUnit == 0
        ? totalWeightInPrimaryUnit
        : totalWeightInPrimaryUnit == 0
        ? dealQuantityInPrimaryUnit
        : Math.min(dealQuantityInPrimaryUnit, totalWeightInPrimaryUnit);

    if (!weightToUse || weightToUse <= 0) {
      return;
    }

    const finalCfrPrice = calculateCFRPrice(
      totalFreightWatch,
      totalFreightCurrencyWatch,
      weightToUse,
      fxRateWatch,
      cfrPriceCurrencyWatch,
      currency1Watch,
      fobPriceWatch,
      fobPriceCurrencyWatch,
      fobPriceWeightUnitWatch,
      cfrPriceWeightUnitWatch,
      primaryUnitWatch
    );
    setValue(cfrPriceManualOverride, false);
    setValue(cifPriceManualOverride, false);
    const currentValue = getValues(cfrPrice);
    if (finalCfrPrice == currentValue) {
      return;
    }
    setValue(cfrPrice, finalCfrPrice);
  }, [
    fobPriceCurrencyWatch,
    fobPriceWatch,
    totalWeightWatch,
    totalWeightUnitWatch,
    totalFreightWatch,
    totalFreightCurrencyWatch,
    cfrPriceCurrencyWatch,
    primaryUnitWatch,
    switchBLCostWatch,
    switchBLCostCurrencyWatch,
    cfrPriceManualOverrideWatch,
    dealQuantityUnitWatch,
    dealQuantityWatch,
    fobPriceWeightUnitWatch,
    cfrPriceWeightUnitWatch
  ]);

  // CIF Price Calculation
  useEffect(() => {
    if (cifPriceManualOverrideWatch) return;

    if (!fxRateWatch) {
      return;
    }
    const finalCIFPrice = calculateCIFPrice(
      fxRateWatch || 1,
      currency1Watch,
      cfrPriceWatch,
      cfrPriceCurrencyWatch,
      insuranceCostWatch,
      cifPriceCurrencyWatch,
      cfrPriceWeightUnitWatch,
      cifPriceWeightUnitWatch,
      primaryUnitWatch
    );
    setValue(cifPriceManualOverride, false);
    setValue(ddpPriceManualOverride, false);
    const currentValue = getValues(cifPrice);
    if (finalCIFPrice == currentValue) {
      return;
    }
    setValue(cifPrice, finalCIFPrice);
  }, [
    insuranceCostWatch,
    cfrPriceWatch,
    cifPriceCurrencyWatch,
    cfrPriceCurrencyWatch,
    cifPriceManualOverrideWatch,
    cfrPriceWeightUnitWatch,
    cifPriceWeightUnitWatch,
    primaryUnitWatch
  ]);

  // Calculate DDP Price
  useEffect(() => {
    if (incotermWatch?.value !== 'DDP') return;
    if (ddpPriceManualOverrideWatch) return;
    if (!fxRateWatch) {
      return;
    }

    const dealQuantityInPrimaryUnit = convertWeight(
      dealQuantityWatch || 0,
      dealQuantityUnitWatch?.value || '',
      primaryUnitWatch?.value
    );

    const totalWeightInPrimaryUnit = convertWeight(
      totalWeightWatch || 0,
      totalWeightUnitWatch?.value,
      primaryUnitWatch?.value
    );

    const weightToUse =
      dealQuantityInPrimaryUnit == 0
        ? Number(totalWeightInPrimaryUnit)
        : totalWeightInPrimaryUnit == 0
        ? Number(dealQuantityInPrimaryUnit)
        : Math.min(Number(dealQuantityInPrimaryUnit), Number(totalWeightInPrimaryUnit));
    if (!weightToUse || weightToUse <= 0) return;

    const finalDdpPrice = calculateDdpPrice(
      cifPriceWatch,
      cifPriceCurrencyWatch,
      cifPriceWeightUnitWatch,
      isfFilingWatch / weightToUse,
      isfFilingCurrencyWatch,
      customClearanceWatch,
      drayageChargeWatch / weightToUse,
      drayageChargeCurrencyWatch,
      fxRateWatch,
      primaryUnitWatch,
      warehousingCostPriceWatch / weightToUse,
      warehousingCostCurrencyWatch,
      ddpPriceWeightUnitWatch,
      ddpPriceCurrencyWatch
    );
    setValue(ddpPriceManualOverride, false);
    const currentValue = getValues(ddpPrice);
    if (finalDdpPrice == currentValue) {
      return;
    }
    setValue(ddpPrice, finalDdpPrice);
  }, [
    cifPriceWatch,
    cifPriceCurrencyWatch,
    cifPriceWeightUnitWatch,
    isfFilingWatch,
    isfFilingCurrencyWatch,
    customClearanceWatch,
    drayageChargeWatch,
    drayageChargeCurrencyWatch,
    fxRateWatch,
    primaryUnitWatch,
    ddpPriceWeightUnitWatch,
    ddpPriceCurrencyWatch,
    warehousingCostPriceWatch,
    warehousingCostCurrencyWatch,
    incotermWatch,
    totalWeightWatch,
    dealQuantityWatch,
    dealQuantityUnitWatch,
    totalWeightUnitWatch
  ]);

  // Quote Selling Price Calculation
  useEffect(() => {
    if (!fxRateWatch) {
      return;
    }
    const weightUnit = getValues(finalQuoteSellingPriceWeightUnit);
    let priceBasedOnIncoterm = 0;
    let currencyBasedOnIncoterm;
    let weightUnitBasedOnIncoterm = DEFAULT_PRIMARY_UNIT;
    switch (incotermWatch?.value) {
      case 'EXW':
        priceBasedOnIncoterm = Number(finalExWorksPriceWatch || 0);
        currencyBasedOnIncoterm = finalExWorksPriceCurrencyWatch;
        weightUnitBasedOnIncoterm = finalExWorksPriceWeightUnitWatch;
        break;
      case 'FOB':
        priceBasedOnIncoterm = Number(fobPriceWatch || 0);
        currencyBasedOnIncoterm = fobPriceCurrencyWatch;
        weightUnitBasedOnIncoterm = fobPriceWeightUnitWatch;
        break;
      case 'CFR':
        priceBasedOnIncoterm = Number(cfrPriceWatch || 0);
        currencyBasedOnIncoterm = cfrPriceCurrencyWatch;
        weightUnitBasedOnIncoterm = cfrPriceWeightUnitWatch;
        break;
      case 'DDP':
        priceBasedOnIncoterm = Number(ddpPriceWatch || 0);
        currencyBasedOnIncoterm = ddpPriceCurrencyWatch;
        weightUnitBasedOnIncoterm = ddpPriceWeightUnitWatch;
        break;
      default:
        priceBasedOnIncoterm = Number(cifPriceWatch || 0);
        currencyBasedOnIncoterm = cifPriceCurrencyWatch;
        weightUnitBasedOnIncoterm = cifPriceWeightUnitWatch;
        break;
    }
    const QuoteSellingPrice = calculateQuotedPrice(
      priceBasedOnIncoterm,
      currencyBasedOnIncoterm,
      weightUnitBasedOnIncoterm,
      additionalCostWatch,
      additionalCostCurrencyWatch,
      switchBLCostWatch,
      switchBLCostCurrencyWatch,
      financeCostWatch,
      profitMarginExpectedWatch,
      discountWatch,
      tariffWatch,
      currency1Watch,
      fxRateWatch ?? 1,
      primaryUnitWatch,
      weightUnit
    );
    setValue(finalQuoteSellingPrice, QuoteSellingPrice);

    const quoteCost = calculateQuoteCost(
      priceBasedOnIncoterm,
      currencyBasedOnIncoterm,
      weightUnitBasedOnIncoterm,
      additionalCostWatch,
      additionalCostCurrencyWatch,
      switchBLCostWatch,
      switchBLCostCurrencyWatch,
      tariffWatch,
      currency1Watch,
      fxRateWatch ?? 1,
      primaryUnitWatch
    );
    const dealQuantityInPrimaryUnit = convertWeight(
      dealQuantityWatch || 0,
      dealQuantityUnitWatch?.value,
      primaryUnitWatch?.value
    );
    const quoteCMValues = calculateCMValues(
      quoteCost,
      // SP as quoted: rounded to 2 decimals in the selling price unit, then converted
      convertPricePerUnit(roundTo2(QuoteSellingPrice), weightUnit?.value, primaryUnitWatch?.value),
      financeCostWatch,
      dealQuantityInPrimaryUnit
    );
    setCmValues(quoteCMValues);
    setValue(cmValuesField, quoteCMValues);
  }, [
    cifPriceWatch,
    cifPriceCurrencyWatch,
    additionalCostWatch,
    additionalCostCurrencyWatch,
    financeCostWatch,
    profitMarginExpectedWatch,
    discountWatch,
    tariffWatch,
    switchBLCostWatch,
    switchBLCostCurrencyWatch,
    incotermWatch,
    primaryUnitWatch,
    finalQuoteSellingPriceWeightUnitWatch,
    ddpPriceWatch,
    ddpPriceCurrencyWatch,
    ddpPriceWeightUnitWatch,
    finalExWorksPriceWeightUnitWatch,
    finalExWorksPriceWatch,
    finalExWorksPriceCurrencyWatch,
    fobPriceWatch,
    fobPriceCurrencyWatch,
    fobPriceWeightUnitWatch,
    cfrPriceWatch,
    cfrPriceCurrencyWatch,
    cfrPriceWeightUnitWatch,
    totalWeightWatch,
    totalWeightUnitWatch,
    dealQuantityWatch,
    dealQuantityUnitWatch,
    primaryUnitWatch,
    fxRateWatch,
    basePriceWatch,
    basePriceCurrencyWatch
  ]);

  const handleProductSelection = (event: any, param: any) => {
    if (param) {
      setValue(productName, param);
    } else {
      setValue(productName, null);
    }
  };

  return (
    <main className={css.mainWrapper}>
      {isEnquiry ? (
        <div className={isExpandedWatch ? css.headerWrapper : css.headerWrapperCollapsed}>
          <div className={css.headerText}>Product {index + 1}</div>
          <div className={css.headerIcons}>
            {handleDuplicateQuote && (
              <Button variant="text" onClick={() => handleDuplicateQuote(index)}>
                Duplicate Product
              </Button>
            )}
            {handleDeleteClick && (
              <ToolTip title="Delete">
                <div>
                  <IconNode
                    src={fieldLength > 1 ? Images.deleteRed : Images.delete}
                    onClick={fieldLength > 1 ? handleDeleteClick : undefined}
                  />
                </div>
              </ToolTip>
            )}
            <ToolTip title={isExpandedWatch ? 'Collapse' : 'Expand'}>
              <div>
                <IconNode
                  src={isExpandedWatch ? Images.arrowUpBig : Images.arrowDownBig}
                  onClick={() => setValue(isExpanded, !isExpandedWatch)}
                />
              </div>
            </ToolTip>
          </div>
        </div>
      ) : null}
      {isExpandedWatch && (
        <div className={css.contentWrapper}>
          <QuoteProductDetails
            productName={productName}
            productOptions={productOptions}
            filterProductOptions={filterProductOptions}
            handleProductSelection={handleProductSelection}
            filter={filter}
            dealQuantity={dealQuantity}
            dealQuantityUnit={dealQuantityUnit}
            selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
            productPackaging={productPackaging}
            moq={moq}
            moqUnit={moqUnit}
            productNameDisable={disableProductName}
            fobPriceManualOverride={fobPriceManualOverride}
            cfrPriceManualOverride={cfrPriceManualOverride}
            cifPriceManualOverride={cifPriceManualOverride}
            exWorksPriceManualOverride={exWorksPriceManualOverride}
            ddpPriceManualOverride={ddpPriceManualOverride}
          />

          <QuoteIncoTerm
            incoTerms={incoTerms}
            incotermWatch={incotermWatch}
            portOfLoading={portOfLoading}
            portOfLoadingOptions={portOfLoadingOptions}
            portOfDischarge={portOfDischarge}
            portOfDischargeOptions={portOfDischargeOptions}
            incoTermsLocation={incoTermsLocation}
            incoTermsZipCode={incoTermsZipCode}
            filterPorts={filterPorts}
            formatPortOptions={formatPortOptions}
            countryOfOrigin={countryOfOrigin}
            materialLocationCountry={materialLocationCountry}
            incoTermsZipCodeWatch={incoTermsZipCodeWatch}
            containerSize={containerSize}
            filterState={filterState}
          />

          <Divider className={css.divider} />

          {!mtt && (
            <QuoteBaseExWorksPrice
              primaryUnitWatch={primaryUnitWatch}
              basePrice={basePrice}
              basePriceCurrency={basePriceCurrency}
              basePriceWeightUnit={basePriceWeightUnit}
              basePriceWeightUnitWatch={basePriceWeightUnitWatch}
              finalExWorksPrice={finalExWorksPrice}
              finalExWorksPriceCurrency={finalExWorksPriceCurrency}
              finalExWorksPriceWeightUnit={finalExWorksPriceWeightUnit}
              finalExWorksPriceWeightUnitWatch={finalExWorksPriceWeightUnitWatch}
              selectedCurrency={selectedCurrency}
              exWorksPriceManualOverride={exWorksPriceManualOverride}
              fobPriceManualOverride={fobPriceManualOverride}
              cfrPriceManualOverride={cfrPriceManualOverride}
              cifPriceManualOverride={cifPriceManualOverride}
              ddpPriceManualOverride={ddpPriceManualOverride}
              selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
            />
          )}

          {incotermWatch?.value !== 'EXW' && !mtt && (
            <QuoteFreightPort
              totalLocalFreight={totalLocalFreight}
              totalLocalFreightCurrency={totalLocalFreightCurrency}
              totalPortAndClearanceExpenses={totalPortAndClearanceExpenses}
              totalPortAndClearanceExpensesCurrency={totalPortAndClearanceExpensesCurrency}
              selectedCurrency={selectedCurrency}
              fobPriceManualOverride={fobPriceManualOverride}
            />
          )}
          {incotermWatch?.value !== 'EXW' && (
            <div className={css.rowWrapper}>
              <QuotePricePackageWrapper
                label={fcl ? 'Total weight in one container' : 'Total weight'}
                unitPlaceholder="Unit"
                inputPlaceholder="Total weight"
                input={totalWeight}
                unit={totalWeightUnit}
                control={control}
                unitOptions={selectedWeightSystemChildOptions}
                inputRequired={true}
                inputOnChange={() => {
                  setValue(fobPriceManualOverride, false, { shouldDirty: true });
                  setValue(cfrPriceManualOverride, false, { shouldDirty: false });
                }}
                unitOnChange={() => {
                  setValue(fobPriceManualOverride, false, { shouldDirty: true });
                  setValue(cfrPriceManualOverride, false, { shouldDirty: false });
                  setValue(cifPriceManualOverride, false, { shouldDirty: false });
                  setValue(ddpPriceManualOverride, false, { shouldDirty: false });
                }}
              />
              <QuotePricePackageWrapper
                label="FOB Price"
                control={control}
                input={fobPrice}
                inputPlaceholder="Enter Price"
                inputOnChange={() => {
                  setValue(ddpPriceManualOverride, false, { shouldDirty: false });
                  setValue(fobPriceManualOverride, true, { shouldDirty: true });
                  setValue(cfrPriceManualOverride, false, { shouldDirty: false });
                  setValue(cifPriceManualOverride, false, { shouldDirty: false });
                  setValue(totalPortAndClearanceExpenses, 0);
                  setValue(totalLocalFreight, 0);
                  setValue(finalExWorksPrice, 0);
                  setValue(basePrice, 0);
                  setValue(finalExWorksPrice, 0);
                }}
                unit={fobPriceWeightUnit}
                unitPlaceholder="Per Unit"
                unitOptions={selectedWeightSystemChildOptions}
                unitOnChange={() => {
                  setValue(fobPriceManualOverride, false, { shouldDirty: true });
                }}
                currency={fobPriceCurrency}
                currencyOptions={selectedCurrency}
                currencyPlaceholder="Currency"
              />
            </div>
          )}
          {incotermWatch?.value !== 'EXW' && incotermWatch?.value !== 'FOB' && (
            <div className={css.rowWrapper}>
              <QuotePricePackageWrapper
                label={fcl ? 'Total International freight per FCL ' : 'Total International freight'}
                currencyPlaceholder="Currency"
                inputPlaceholder="Enter Price"
                input={totalFreight}
                currency={totalFreightCurrency}
                control={control}
                currencyOptions={selectedCurrency}
                inputOnChange={() => {
                  setValue(cfrPriceManualOverride, false, { shouldDirty: true });
                }}
              />

              <QuotePricePackageWrapper
                label="CFR Price"
                control={control}
                input={cfrPrice}
                inputPlaceholder="Enter Price"
                inputOnChange={() => {
                  setValue(cfrPriceManualOverride, true, { shouldDirty: true });
                  setValue(cifPriceManualOverride, false, { shouldDirty: false });
                  setValue(basePrice, 0);
                  setValue(finalExWorksPrice, 0);
                  setValue(fobPrice, 0);
                  setValue(totalPortAndClearanceExpenses, 0);
                  setValue(totalLocalFreight, 0);
                  setValue(totalFreight, 0);
                  // setValue(totalWeight, 0);
                  setValue(ddpPriceManualOverride, false, { shouldDirty: false });
                }}
                unit={cfrPriceWeightUnit}
                unitPlaceholder="Per Unit"
                unitOptions={selectedWeightSystemChildOptions}
                unitOnChange={() => {
                  setValue(cfrPriceManualOverride, false, { shouldDirty: true });
                }}
                currency={cfrPriceCurrency}
                currencyOptions={selectedCurrency}
                currencyPlaceholder="Currency"
              />
            </div>
          )}
          {incotermWatch?.value !== 'EXW' &&
            incotermWatch?.value !== 'FOB' &&
            incotermWatch?.value !== 'CFR' && (
              <div className={css.rowWrapper}>
                <Controller
                  name={insuranceCost}
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={
                        <span>
                          Credit and Cargo insurance cost
                          <Typography variant="label"> ( % )</Typography>
                        </span>
                      }
                      placeholder="Enter value in percentage"
                      onChange={(e) => {
                        field.onChange(e);
                        setValue(cifPriceManualOverride, false, { shouldDirty: false });
                      }}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      disabled
                    />
                  )}
                />
                <QuotePricePackageWrapper
                  label="CIF Price"
                  control={control}
                  input={cifPrice}
                  inputPlaceholder="Enter Price"
                  inputOnChange={() => {
                    setValue(cifPriceManualOverride, true, { shouldDirty: true });
                    setValue(cfrPrice, 0);
                    setValue(basePrice, 0);
                    setValue(finalExWorksPrice, 0);
                    setValue(fobPrice, 0);
                    setValue(totalPortAndClearanceExpenses, 0);
                    setValue(totalLocalFreight, 0);
                    setValue(totalFreight, 0);
                    setValue(ddpPriceManualOverride, false, { shouldDirty: false });
                  }}
                  unit={cifPriceWeightUnit}
                  unitPlaceholder="Per Unit"
                  unitOptions={selectedWeightSystemChildOptions}
                  unitOnChange={() => {
                    setValue(cifPriceManualOverride, false, { shouldDirty: true });
                  }}
                  currency={cifPriceCurrency}
                  currencyOptions={selectedCurrency}
                  currencyPlaceholder="Currency"
                />
              </div>
            )}

          {(incotermWatch?.value == 'DDP' || incotermWatch?.value == 'DAP') && (
            <QuoteDdpPrice
              ddpPrice={ddpPrice}
              ddpPriceCurrency={ddpPriceCurrency}
              ddpPriceWeightUnit={ddpPriceWeightUnit}
              ddpPriceManualOverride={ddpPriceManualOverride}
              ddpPriceWeightUnitWatch={ddpPriceWeightUnitWatch}
              drayageCharge={drayageCharge}
              drayageChargeCurrency={drayageChargeCurrency}
              isfFiling={isfFiling}
              isfFilingCurrency={isfFilingCurrency}
              customClearance={customClearance}
              warehousingCostPrice={warehousingCostPrice}
              warehousingCostCurrency={warehousingCostCurrency}
              selectedCurrency={selectedCurrency}
              selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
              additionalCost={additionalCost}
              additionalCostCurrency={additionalCostCurrency}
              switchBlCost={switchBlCost}
              switchBLCostCurrency={switchBLCostCurrency}
              primaryUnitWatch={primaryUnitWatch}
              filterState={filterState}
              cifPriceManualOverride={cifPriceManualOverride}
              insuranceCost={insuranceCost}
              cfrPrice={cfrPrice}
              basePrice={basePrice}
              finalExWorksPrice={finalExWorksPrice}
              fobPrice={fobPrice}
              totalPortAndClearanceExpenses={totalPortAndClearanceExpenses}
              totalLocalFreight={totalLocalFreight}
              totalFreight={totalFreight}
              totalWeight={totalWeight}
              cifPrice={cifPrice}
            />
          )}

          {!(incotermWatch?.value == 'DDP' || incotermWatch?.value == 'DAP') && (
            <div className={css.rowWrapper}>
              <QuotePricePackageWrapper
                label={
                  <span>
                    Additional Cost
                    {primaryUnitWatch && (
                      <Typography variant="label"> ( Per {primaryUnitWatch?.label} )</Typography>
                    )}
                  </span>
                }
                currencyPlaceholder="Currency"
                inputPlaceholder="Enter Price"
                input={additionalCost}
                currency={additionalCostCurrency}
                control={control}
                currencyOptions={selectedCurrency}
              />
              {mtt && (
                <QuotePricePackageWrapper
                  label="Switch BL Cost"
                  currencyPlaceholder="Currency"
                  inputPlaceholder="Enter Price"
                  input={switchBlCost}
                  currency={switchBLCostCurrency}
                  control={control}
                  currencyOptions={selectedCurrency}
                />
              )}
            </div>
          )}
          <Divider className={css.divider} />
          <div className={css.rowWrapper}>
            <Controller
              name={financeCost}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={
                    <span>
                      Finance cost
                      <Typography variant="label"> ( % )</Typography>
                    </span>
                  }
                  placeholder="Enter value in percentage"
                  error={fieldState.invalid}
                  helperText={
                    fieldState.error?.message ??
                    (currency1Watch?.label
                      ? getCommaSeperatedCurrencyAmount(currency1Watch?.label, cmValues.financeCost)
                      : undefined)
                  }
                />
              )}
            />
            <Controller
              name={profitMarginExpected}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={<span>Expected Profit Margin %</span>}
                  required
                  placeholder="Enter value in percentage"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </div>
          <div className={css.rowWrapper}>
            <Controller
              name={discount}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={
                    <span>
                      Discount
                      <Typography variant="label"> ( % )</Typography>
                    </span>
                  }
                  placeholder="Enter value in percentage"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name={tariff}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>
                        Tariff
                        <Typography variant="label"> ( % )</Typography>
                      </span>
                      <ToolTip
                        title="Applicable for US enquires. This will be added along with additional charges"
                        placement="right"
                      >
                        <div>
                          <IconNode src={Images.infoGrey} width={16} height={16} />
                        </div>
                      </ToolTip>
                    </div>
                  }
                  placeholder="Enter value in percentage"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default NonConsolidationQuote;
